import { useCallback } from "react";
import cx from "classnames";

import { servicePostValhallaRoute } from "../../api/serviceRoute";
import "./routebuilder.css";

import {
  BsBezier2,
  BsGeoAlt,
  BsPlusLg,
  BsTrash,
  BsCompass,
  BsMap,
  BsCheckCircle,
  BsLayers,
  BsArrowRightShort,
} from "react-icons/bs";

const RouteBuilder = ({
  routeDraft,
  setRouteDraft,
  waypointDraft,
  selectingPoint,

  onRouteNameChange,
  onRouteDescriptionChange,

  onWaypointNameChange,
  onWaypointAddressChange,

  onSelectPoint,
  onAddWaypoint,
  onRemoveWaypoint,

  normalizeValhallaRoute,
}) => {
  const selectedCoordinates = waypointDraft.location?.coordinates;

  const isAddDisabled = !waypointDraft.name.trim() || !waypointDraft.location;
  const isGenerateDisabled = routeDraft.waypoints.length < 2;

  const calculateRoute = useCallback(async () => {
    console.log("route: \n ", { routeDraft });

    if (routeDraft.waypoints.length < 2) {
      return;
    }

    const payload = {
      locations: routeDraft.waypoints.map((waypoint) => {
        const [lng, lat] = waypoint.location.coordinates;

        return {
          lat,
          lon: lng,
          type: "break",
          name: waypoint.name,
        };
      }),

      costing: routeDraft.routing.costing,
      costing_options: routeDraft.routing.costingOptions,
      directions_options: {
        units: "kilometers",
      },
    };

    try {
      const result = await servicePostValhallaRoute(payload);
      // console.log("Valhalla route: ", result.trip);

      const route = normalizeValhallaRoute(result, routeDraft.waypoints);

      setRouteDraft((prev) => ({
        ...prev,
        route,
      }));

      console.log("Normalized route:", route);
    } catch (error) {
      console.error("Failed to calculate route: ", error);
    }
  }, [routeDraft]);

  return (
    <aside className="route-builder">
      {/* Header */}
      <header className="route-builder-header">
        <div className="header-icon">
          <BsBezier2 size={20} />
        </div>
        <div>
          <h2>Create Vehicle Route</h2>
          <p className="header-subtitle">
            Configure stops and calculate optimal navigation
          </p>
        </div>
      </header>

      <div className="route-builder-scroll-area">
        {/* Route Overview */}
        <section className="route-builder-card">
          <div className="card-header">
            <BsLayers size={16} />
            <h3>General Info</h3>
          </div>

          <div className="field-group">
            <label htmlFor="route-name" className="route-builder-label">
              Route Name
            </label>
            <input
              id="route-name"
              type="text"
              value={routeDraft.name}
              onChange={(e) => onRouteNameChange(e.target.value)}
              placeholder="e.g. Express Route 41"
              className="route-builder-input"
            />
          </div>

          <div className="field-group">
            <label htmlFor="route-desc" className="route-builder-label">
              Description
            </label>
            <textarea
              id="route-desc"
              value={routeDraft.description}
              onChange={(e) => onRouteDescriptionChange(e.target.value)}
              placeholder="Provide context or instructions for this route..."
              className="route-builder-textarea"
              rows={2}
            />
          </div>
        </section>

        {/* Add Stop Panel */}
        <section className="route-builder-card">
          <div className="card-header">
            <BsGeoAlt size={16} />
            <h3>Add Route Stop</h3>
          </div>

          <div className="field-group">
            <label className="route-builder-label">Stop Name</label>
            <input
              type="text"
              value={waypointDraft.name}
              onChange={(e) => onWaypointNameChange(e.target.value)}
              placeholder="e.g. Central Station North"
              className="route-builder-input"
            />
          </div>

          <div className="field-group">
            <label className="route-builder-label">Address / Landmark</label>
            <input
              type="text"
              value={waypointDraft.address}
              onChange={(e) => onWaypointAddressChange(e.target.value)}
              placeholder="e.g. 124 Market Street"
              className="route-builder-input"
            />
          </div>

          {/* Map Picker CTA */}
          <div className="map-picker-wrapper">
            <button
              type="button"
              className={cx("btn-map-select", { active: selectingPoint })}
              onClick={onSelectPoint}
            >
              <BsCompass size={16} className={cx({ spin: selectingPoint })} />
              <span>{selectingPoint ? "Click on Map..." : "Pin on Map"}</span>
            </button>

            <div className="coordinate-badge">
              {selectedCoordinates ? (
                <>
                  <span className="coord-chip">
                    Lat: {selectedCoordinates[1].toFixed(4)}
                  </span>
                  <span className="coord-chip">
                    Lng: {selectedCoordinates[0].toFixed(4)}
                  </span>
                </>
              ) : (
                <span className="coord-placeholder">No coordinates pinned</span>
              )}
            </div>
          </div>

          <button
            type="button"
            className="btn-primary btn-add-stop"
            onClick={onAddWaypoint}
            disabled={isAddDisabled}
          >
            <BsPlusLg size={14} />
            <span>Add Stop to Route</span>
          </button>
        </section>

        {/* Waypoints List */}
        <section className="route-builder-card">
          <div className="card-header space-between">
            <div className="card-title-group">
              <BsMap size={16} />
              <h3>Route Sequence</h3>
            </div>
            <span className="counter-badge">
              {routeDraft.waypoints.length} Stops
            </span>
          </div>

          {routeDraft.waypoints.length === 0 ? (
            <div className="route-builder-empty">
              <BsGeoAlt size={28} className="empty-icon" />
              <p>No stops added yet.</p>
              <span>
                Pin locations on the map or type details above to begin.
              </span>
            </div>
          ) : (
            <div className="route-builder-stop-list">
              {routeDraft.waypoints.map((waypoint, index) => {
                const isFirst = index === 0;
                const isLast = index === routeDraft.waypoints.length - 1;

                let badgeClass = "badge-stop";
                let waypointType = "Stop";
                if (isFirst) {
                  waypointType = "Start";
                  badgeClass = "badge-start";
                } else if (isLast) {
                  waypointType = "End";
                  badgeClass = "badge-end";
                }

                const [lng, lat] = waypoint.location.coordinates;

                return (
                  <div key={waypoint.waypointId} className="stop-card">
                    <div className="stop-card-timeline">
                      <div className={cx("stop-number", badgeClass)}>
                        {index + 1}
                      </div>
                      {!isLast && <div className="timeline-connector" />}
                    </div>

                    <div className="stop-card-content">
                      <div className="stop-card-details">
                        <strong className="stop-name">{waypoint.name}</strong>

                        {waypoint.address && (
                          <p className="stop-address">{waypoint.address}</p>
                        )}

                        <div className="stop-coords">
                          {lat.toFixed(5)}, {lng.toFixed(5)}
                        </div>
                      </div>

                      <div className="stop-action">
                        <span className={cx("type-badge", badgeClass)}>
                          {waypointType}
                        </span>

                        <button
                          type="button"
                          className="btn-icon-danger"
                          onClick={() => onRemoveWaypoint(waypoint.waypointId)}
                          title="Remove stop"
                        >
                          <BsTrash />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {/* Action Footer */}
      <footer className="route-builder-footer">
        <button
          type="button"
          className="btn-accent btn-generate"
          disabled={isGenerateDisabled}
          onClick={calculateRoute}
        >
          <BsCheckCircle size={18} />
          <span>Generate Route Path</span>
        </button>
      </footer>
    </aside>
  );
};

export default RouteBuilder;
